import { call, put, takeEvery, select, takeLeading } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";
import Debug from "debug";
import axios from "axios";

import { ACTIONS } from "../actions/constants";
import { extractResponse } from "./helpers";
import {
  ErrorState,
  Stream,
  RequestResponse,
  ActionChannelGetAll,
  ActionChannelGetOne,
  ActionBroadcastSettings,
  BroadcastSettings,
  ActionChannelDeleteOne,
  ActionChannelCreateOne,
  ActionChannelEditOne,
} from "../types";

import { RootState } from "../reducers";
import { getErrorObj } from "../utils/errorUtils";

const debug = Debug("broadcast:sagas:channels");

const API_URL = process.env.REACT_APP_API_BASE_URL;

const channelsGetAllEndpoint = (orgId: string, spaceId: string): string =>
  `${API_URL}/${orgId}/${spaceId}/live-streams`;

const channelGetOneEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string => `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}`;

const channelsDeleteOneEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string => `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}`;

const channelsCreateOneEndpoint = (): string => `${API_URL}/live-streams`;

const channelEditOneEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string => `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}`;

const broadcastSettingsEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string =>
  `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}/settings`;

const getStudioToken = (state: RootState): string => state.studio;

export function* channelsGetAll({
  payload,
  logger,
}: ActionChannelGetAll): SagaIterator {
  if (payload !== undefined) {
    const { orgId, spaceId } = payload;

    try {
      const studio = yield select(getStudioToken);
      const { token } = studio;
      const result: RequestResponse = yield call(
        axios.get,
        channelsGetAllEndpoint(orgId, spaceId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      debug("get channels result", result);
      const data = extractResponse(result.data);
      if (data) {
        const channels: Stream[] = data.channels;
        yield put({
          type: ACTIONS.CHANNELS_GET_ALL_SUCCESS,
          payload: channels,
        });
      } else {
        debug("get channels failure");
        yield put({
          type: ACTIONS.CHANNELS_GET_ALL_FAILURE,
          payload: { error: ErrorState.FailedToFetchStreams },
        });
      }
    } catch (error) {
      debug(error);
      logger?.error(ErrorState.FailedToFetchStreams, getErrorObj(error));
      yield put({
        type: ACTIONS.CHANNELS_GET_ALL_FAILURE,
        payload: { error: error.message },
      });
    }
  }
}

function* broadcastSettings({
  payload,
  logger,
}: ActionBroadcastSettings): SagaIterator {
  if (payload !== undefined) {
    const { orgId, spaceId, channelId } = payload;
    const studio = yield select(getStudioToken);
    const { token } = studio;
    const result: RequestResponse = yield call(
      axios.get,
      broadcastSettingsEndpoint(orgId, spaceId, channelId),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    debug("broadcastSettings result", result);
    const data = extractResponse(result.data);
    if (data) {
      const settings: BroadcastSettings = data.settings;
      yield put({
        type: ACTIONS.BROADCAST_SETTINGS_SUCCESS,
        payload: settings,
      });
    } else {
      debug("broadcastSettings failure");
      const errorMsg = ErrorState.FailedToFetchBroadcastSettings;
      logger?.error(errorMsg);
      yield put({
        type: ACTIONS.BROADCAST_SETTINGS_FAILURE,
        payload: { error: errorMsg },
      });
    }
  }
}

export function* channelsGetOne({
  payload,
  logger,
}: ActionChannelGetOne): SagaIterator {
  debug("channelsGetOne", payload);
  if (payload !== undefined) {
    const { orgId, spaceId, channelId } = payload;
    const studio = yield select(getStudioToken);
    const { token } = studio;

    try {
      const result: RequestResponse = yield call(
        axios.get,
        channelGetOneEndpoint(orgId, spaceId, channelId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = extractResponse(result.data);
      debug(data);
      if (data) {
        const channel: Stream = data.channel;
        yield put({
          type: ACTIONS.CHANNELS_GET_ONE_SUCCESS,
          payload: channel,
        });
      } else {
        logger?.error("Failed to get one channel (no response data)", {
          channelId: payload.channelId,
          result,
        });
        yield put({
          type: ACTIONS.CHANNELS_GET_ONE_FAILURE,
          payload: { error: ErrorState.FailedToFetchStream },
        });
      }
    } catch (error) {
      debug(error);
      logger?.error("Failed to get one channel (catch)", {
        channelId: payload.channelId,
        error: getErrorObj(error),
      });
      yield put({
        type: ACTIONS.CHANNELS_GET_ONE_FAILURE,
        payload: { error: error.message },
      });
    }
  }
}

export function* channelsDeleteOne({
  payload,
  logger,
}: ActionChannelDeleteOne): SagaIterator {
  debug("channelsDeleteOne", payload);
  if (payload !== undefined) {
    const { orgId, spaceId, channelId } = payload;
    const studio = yield select(getStudioToken);
    const { token } = studio;

    try {
      const result: RequestResponse = yield call(
        axios.delete,
        channelsDeleteOneEndpoint(orgId, spaceId, channelId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      debug("delete channel result", result);
      const data = extractResponse(result.data);
      if (data) {
        yield put({
          type: ACTIONS.CHANNELS_DELETE_ONE_SUCCESS,
          payload,
        });
      } else {
        debug("delete channel failure");
        logger?.error("Failed to delete one channel (no response data)", {
          channelId: payload.channelId,
          result,
        });
        yield put({
          type: ACTIONS.CHANNELS_DELETE_ONE_FAILURE,
          payload: { error: ErrorState.FailedToDeleteStream },
        });
      }
    } catch (error) {
      debug(error);
      logger?.error("Failed to delete one channel (catch)", {
        channelId: payload.channelId,
        error: getErrorObj(error),
      });
      yield put({
        type: ACTIONS.CHANNELS_DELETE_ONE_FAILURE,
        payload: { error: error.message },
      });
    }
  }
}

export function* channelsCreateOne({
  payload,
  logger,
}: ActionChannelCreateOne): SagaIterator {
  debug("channelsCreateOne", payload);
  if (payload !== undefined) {
    const studio = yield select(getStudioToken);
    const { token } = studio;

    try {
      const result: RequestResponse = yield call(
        axios.post,
        channelsCreateOneEndpoint(),
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      debug("create channel result", result);
      const data = extractResponse(result.data);
      if (data) {
        yield put({
          type: ACTIONS.CHANNELS_CREATE_ONE_SUCCESS,
          payload: data.channel,
        });
      } else {
        debug("create channel failure");
        yield put({
          type: ACTIONS.CHANNELS_CREATE_ONE_FAILURE,
          payload: { error: ErrorState.FailedCreateStream },
        });
      }
    } catch (error) {
      if (error.response !== undefined) {
        const { data } = error.response;
        const { success, error: responseError } = data;
        const errorMsg =
          success && responseError === ErrorState.MaximumNumberOfStreamsReached
            ? ErrorState.MaximumNumberOfStreamsReached
            : ErrorState.FailedCreateStream;
        debug("error?", responseError);
        logger?.error("Failed to create one channel (catch)", {
          error: responseError,
          errorObj: getErrorObj(error),
          errorResponseSuccess: success,
          displayingErrorText: errorMsg,
        });
        yield put({
          type: ACTIONS.CHANNELS_CREATE_ONE_FAILURE,
          payload: {
            error: errorMsg,
          },
        });
      } else {
        logger?.error(
          "Failed to create one channel (no response data)",
          getErrorObj(error)
        );
        yield put({
          type: ACTIONS.CHANNELS_CREATE_ONE_FAILURE,
          payload: { error: error.message },
        });
      }
    }
  }
}

export function* channelsEditOne({
  payload,
  logger,
}: ActionChannelEditOne): SagaIterator {
  debug("channelsEditOne", payload);
  if (payload !== undefined) {
    const { screenCloudOrgId, spaceId, channelId } = payload;
    const studio = yield select(getStudioToken);
    const { token } = studio;

    try {
      const result: RequestResponse = yield call(
        axios.post,
        channelEditOneEndpoint(screenCloudOrgId, spaceId, channelId),
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      debug("edit channel result", result);
      const data = extractResponse(result.data);
      if (data) {
        yield put({
          type: ACTIONS.CHANNELS_EDIT_ONE_SUCCESS,
          payload: data.channel,
        });
      } else {
        logger?.error("Failed to edit one channel (no response data)", {
          channelId: payload.channelId,
          result,
        });
        debug("edit channel failure");
        yield put({
          type: ACTIONS.CHANNELS_EDIT_ONE_FAILURE,
          payload: { error: ErrorState.FailedUpdateStream },
        });
      }
    } catch (error) {
      debug(error);
      logger?.error("Failed to edit one channel (catch)", {
        channelId: payload.channelId,
        error: getErrorObj(error),
      });
      yield put({
        type: ACTIONS.CHANNELS_EDIT_ONE_FAILURE,
        payload: {
          error: ErrorState.FailedUpdateStream,
        },
      });
    }
  }
}

export function* channelsGetAllSaga(): SagaIterator {
  yield takeEvery(ACTIONS.CHANNELS_GET_ALL_REQUEST, channelsGetAll);
}

export function* channelsGetOneSaga(): SagaIterator {
  yield takeEvery(ACTIONS.CHANNELS_GET_ONE_REQUEST, channelsGetOne);
}

export function* broadcastSettingsSaga(): SagaIterator {
  yield takeLeading(ACTIONS.BROADCAST_SETTINGS_REQUEST, broadcastSettings);
}

export function* channelDeleteOneSaga(): SagaIterator {
  yield takeLeading(ACTIONS.CHANNELS_DELETE_ONE_REQUEST, channelsDeleteOne);
}

export function* channelCreateOneSaga(): SagaIterator {
  yield takeLeading(ACTIONS.CHANNELS_CREATE_ONE_REQUEST, channelsCreateOne);
}

export function* channelEditOneSaga(): SagaIterator {
  yield takeLeading(ACTIONS.CHANNELS_EDIT_ONE_REQUEST, channelsEditOne);
}
